import { Close } from "@mui/icons-material";
import { Button, CircularProgress, Modal } from "@mui/material";
import { ReactElement, createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCapacitor } from "@/hooks/useCapacitor";

const context = createContext<{
    setPopup: (content: ReactElement | null, options?: ModalOptions) => void;
} | null>(null);

type ModalOptions = {
    title?: string;
    size?: "sm" | "md" | "lg" | "xl";
    onClose?: (closeData?: unknown) => void;
    closeData?: unknown;
};

export const PopupProvider = ({ children }: { children: ReactElement }) => {
    const [content, setPopupContent] = useState<ReactElement | null>(null);
    const [options, setOptions] = useState<ModalOptions | null>(null);

    const { t } = useTranslation();
    const { isCordova } = useCapacitor();

    const setPopup = (newContent: ReactElement | null, newOptions?: ModalOptions) => {
        if (!newContent) {
            options?.onClose && options.onClose(newOptions?.closeData || null);
        }

        if (!newContent || !newOptions) {
            setOptions(null);
        } else if (newOptions) {
            setOptions(newOptions);
        }

        setPopupContent(newContent);
    };

    const handleClose = () => setPopup(null);

    const size = options?.size || "md";

    const sizes = {
        sm: "md:w-1/2 lg:w-1/3",
        md: "md:w-2/3 lg:w-1/2",
        lg: "md:w-4/5 lg:w-3/4",
        xl: "md:w-5/6 lg:w-4/5",
    };

    return (
        <context.Provider
            value={{
                setPopup,
            }}
        >
            {children}
            <Modal open={!!content} onClose={handleClose} className="flex justify-center items-center">
                <div
                    className={`flex flex-col bg-white w-full h-full ${
                        sizes[size]
                    } md:h-[75vh] outline-none shadow-lg md:rounded-3xl overflow-hidden ${isCordova ? "pt-28" : ""}`}
                >
                    <div className="p-2">
                        <div className="sticky z-50 top-0 bg-elitDarkBlue w-full h-20 flex justify-between items-center rounded-2xl">
                            <div className="ml-4 text-white">{options?.title}</div>
                            <div className="mr-4">
                                <Button onClick={handleClose}>
                                    <span className="text-white font-bold">{t("cancel")}</span>
                                    <Close className="text-white" />
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col h-full w-full overflow-scroll">
                        <div className="p-4 relative h-full">{content || <></>}</div>
                    </div>
                </div>
            </Modal>
        </context.Provider>
    );
};

export const PopupLoader = ({ isLoading }: { isLoading: boolean }) => (
    <div className={`z-40 overflow-hidden rounded-2xl absolute inset-2 pointer-events-none`}>
        <div
            className={`flex justify-center items-center bg-elitDarkBlue transition-all w-full ${
                isLoading ? "h-full" : "h-0"
            }`}
        >
            {isLoading && <CircularProgress sx={{ color: "white" }} />}
        </div>
    </div>
);

const usePopup = () => {
    const ctx = useContext(context);
    if (!ctx) {
        throw new Error("usePopup must be used within a PopupProvider");
    } else {
        return ctx;
    }
};

export default usePopup;
